define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"lodash",
	"mobx",
	"src/c8-platform/type/value",
	"csi/action",
	"csi/action/Controller",
	"csi/def/Action",
	"csi/heading/_base",
	"csi/html",
	"csi/image/_base",
	"csi/middleware/lightbox/Lightbox",
	"csi/model/image",
	"csi/service/nodeService",
	"csi/service/writerService",
	"csi/state/Cache",
	"csi/state/rootStore",
	"csi/types",
	"csi/utility/htmlUtility",
	"csi/xml",
	"csi/xml/Writer"
], function(declare, lang, _, {runInAction}, {attributeValueAsArray}, csiAction, Controller, Action, csiHeading, html, csiImage, Lightbox,
	{ATTR_IMAGES, TYPE_IMAGE, getDefaultImage, getImageContainerNumberedKeys, getImageData, getIndexOfKeyInImageData, isImageContainer},
	{resolveNodes}, {submitWriter}, {rootCache}, {browserStore, routerStore, sessionStore}, types, {getDialogZIndex}, xml, Writer) {

	return declare(Controller, {
		/** @property {boolean} restrictEdit - indicates if image cannot be edited */
		restrictEdit: false,

		/** @property {Set<cnl>} _setEditedImages - set of image urls that have been edited/replaced */
		_setEditedImages: null,

		/**
		 * Return list of images to display in lightbox and index of image to show first
		 * @return {Promise<[images: ImageData[], index: number]>}
		 */
		async _getImages() {
			const {m_element, heading, ren, _ctx} = this;
			const {cache, defView, keyValue} = _ctx;
			const node = _ctx.getNode();

			if (heading.m_format !== types.FORMAT_REFERENCED_IMAGE) {
				// show all images in heading if available; it is possible that ren is a container renderer and we could use
				// the base renderer to get the image information, but in cases such as this, all images in container should
				// already be rendered and image information can be extracted from DOM below
				if (ren?.toViewable) {
					const attrId = heading.m_id;

					// extract images from image container
					if (isImageContainer(node, attrId)) {
						const value = node[ATTR_IMAGES];
						await resolveNodes(Object.values(value), cache);
						const urlImage = _ctx.hasContainerKey() ? value[keyValue] : getDefaultImage(node);
						const imageKey = csiImage.getNumberedKey(value, urlImage);
						const images = getImageContainerNumberedKeys(value).map((imageKey) => getImageData(cache.get(value[imageKey]), node, ATTR_IMAGES, imageKey));
						const index = getIndexOfKeyInImageData(images, imageKey);
						return [images, index];
					}

					// extract images from arbitrary attribute (either image itself, or node referencing image - i.e. User.Image)
					const value = (node.$Type === TYPE_IMAGE) ? node.$URL : node[attrId];
					if (value) {
						// make sure all images are resolved
						await resolveNodes(attributeValueAsArray(value), cache);

						if (typeof value === "string") {
							// Image Browser images that aren't turntable images should be edited.
							// Also prevent the lightbox editor from incorrectly targeting the thumbnail as the attribute ID to edit
							return defView.m_id == "ImageBrowser" ?
								[[getImageData(cache.get(value), null, null, null, null, null, true)], 0] :
								[[getImageData(cache.get(value), node, attrId, keyValue)], 0];
						}

						const images = _.map(value, (value, key) => getImageData(cache.get(value), node, attrId, key));
						const validImages = _.compact(images);
						const index = getIndexOfKeyInImageData(validImages, keyValue);
						return [validImages, index];
					}
				}
			}

			// otherwise show images based on those that can be found in the DOM
			let refNode = this.refNode || csiHeading.findParentHeadingNode(m_element);

			// when rendering sub-heading, we want to go up to heading level to include all images
			if (refNode == m_element) {
				const parentNode = csiHeading.findParentHeadingNode(refNode.parentNode);
				const key1 = html.getDataAttribute(m_element, html.DATA_HEADING);
				const key2 = html.getDataAttribute(parentNode, html.DATA_HEADING);

				if (key1 === key2) {
					refNode = parentNode;
				}
			}

			let imageUrl = html.getDataAttribute(refNode, html.DATA_IMG_URL);
			let imageContainerUrl = html.getDataAttribute(refNode, html.DATA_IMG_CONTAINER_URL);
			let imageContainerUrls;
			let imageUrls;
			let index = 0;

			if (imageUrl) {
				// show image clicked on
				imageUrls = [imageUrl];
				imageContainerUrls = [imageContainerUrl];
			} else {
				// find index of image clicked on when there are many
				const domNodes = Array.from(html.queryDom([html.getDataAttributeSelector(html.DATA_IMG_URL)], refNode));
				index = domNodes.indexOf(m_element);
				imageUrls = domNodes.map((domNode) => html.getDataAttribute(domNode, html.DATA_IMG_URL));
				imageContainerUrls = domNodes.map((domNode) => html.getDataAttribute(domNode, html.DATA_IMG_CONTAINER_URL));
			}

			// finally generate image data
			const imageNodes = await resolveNodes(imageUrls, cache, true);
			const images = imageNodes.map((imageNode, i) => getImageData(imageNode, cache.get(imageContainerUrls[i])));

			return [images, index];
		},

		/**
		 * @override
		 * Handle case where showing single image of node and we would like to show all in lightbox
		 */
		async _preAction() {
			const {m_cache, heading, _ctx} = this;

			if (heading && _ctx) {
				const value = _ctx.getNodeAttributeValue(heading.m_id);
				const urls = _.values(value);
				await resolveNodes(urls, m_cache);
			}
		},

		async onBackgroundColorChange(color, recentCustomColors = []) {
			const {userPreferences} = sessionStore;
			const lightBoxSelectedColor = userPreferences.LightBoxSelectedColor;
			const colorPickerCustomColors = userPreferences.LightBoxColorPickerCustomColors;
			const writer = new Writer();
			const cn = writer.changeNode(userPreferences.$URL);
			let changesMade = false;

			// Did the color change? Update user preferences.
			if (color !== lightBoxSelectedColor) {
				cn.changeAttribute("LightBoxSelectedColor", xml.ATTR_STRING, color);
				changesMade = true;
			}

			// Did the list of custom colors changed? Update user preferences.
			if (recentCustomColors.some((color, index) => color !== colorPickerCustomColors[index])) {
				cn.changeAttribute("LightBoxColorPickerCustomColors", xml.ATTR_STRINGVECTOR, recentCustomColors);
				changesMade = true;
			}

			if (changesMade) {
				try {
					const response = await submitWriter(writer);
					rootCache.cacheResponse(response);
				} catch (err) {
					console.warn("Failed to save changes to the C8 Node.", err);
				}
			}
		},

		/**
		 * Edit given image
		 * @param {ImageData} imageData
		 * @param {LightboxEditCompleteFunction} onComplete
		 */
		_onEdit(imageData, onComplete) {
			const {m_cache, m_element, widget} = this;
			const popupAction = lang.mixin(new Action(), {
				bKeepViewEnabled: true,
				classController: "csi/image/ImageEditorController",
				m_id: "ImageEditImage",
				m_postAction: (ac) => {
					// indicate that image has been edited for post-processing
					this._setEditedImages.add(imageData.url);

					// update the lightbox store
					onComplete(m_cache.get(ac.newImageUrl), m_cache.get(imageData.containerUrl), ac.replaced);

					// resume lightbox controller
					setTimeout(() => {
						this.resumeProcess();
						this.postProcess();
					}, 1);
				}
			});

			popupAction.resolve();

			csiAction.invoke(widget, m_element, popupAction, {
				imageData,
				m_cache,
			});
		},

		/**
		 * Open lightbox with viewable image(s)
		 */
		async process() {
			const [images, startIndex] = await this._getImages();
			const validImages = _.compact(images);
			const {userPreferences} = sessionStore;
			const lightBoxSelectedColor = userPreferences.LightBoxSelectedColor;
			const colorPickerCustomColors = userPreferences.LightBoxColorPickerCustomColors;

			if (validImages.length) {
				// restrict editing in Lightbox based on context
				if (this.restrictEdit || this._ctx.bReadOnlyView) {
					validImages.forEach((imageData) => {
						imageData.editableAttributes = false;
						imageData.editableImage = false;
					});
				}

				this.suspendProcess();
				this._setEditedImages = new Set();

				// set global state to stop ability to open Lightbox recursively
				runInAction(() => {
					browserStore.restrictLightbox = true;
				});

				routerStore.openDialog(Lightbox.default, {
					backgroundColor: lightBoxSelectedColor,
					images: validImages,
					onBackgroundColorChange: this.onBackgroundColorChange,
					onChange: (oldImageUrl, newImageUrl, updatedNodeResponse) => {
						this._setEditedImages.add(oldImageUrl);
						this.m_cache.cacheResponse(updatedNodeResponse);
					},
					onClose: () => {
						runInAction(() => {
							browserStore.restrictLightbox = false;
						});

						this.resumeProcess();
						this.postProcess(true);
					},
					onEdit: (imageData, onReplace) => this._onEdit(imageData, onReplace),
					recentCustomColors: colorPickerCustomColors,
					startIndex,
					zIndex: getDialogZIndex(),
				});
			} else {
				this.postProcess();
			}
		},

		/**
		 * Refresh view with new images
		 */
		postProcessSuccess() {
			this.widget?.redrawImages([...this._setEditedImages]);
		}
	});
});